import { createFormatter, createListFormatter, createSubObjectFormatter } from 'reacticoon/format'

import moment from 'moment'
import sortBy from 'lodash/sortBy'
import map from 'lodash/map'
import forEach from 'lodash/forEach'
import isString from 'lodash/isString'
import isEmpty from 'lodash/isEmpty'
import { findOnArray } from 'reacticoon/utils/array'
import { formatPrice } from 'modules/currency/format'
import { formatUser } from 'modules/user/format'
import { getJobData } from 'modules/jobGrades/constants'
import { millisecondsToStr } from 'modules/date'
import { formatPlayerAndRconPlayer } from 'modules/playerAndRconPlayer/format'
import { getGroupLabel, Group } from './constants'
import { formatInventory, formatInventoryInventory } from 'modules/inventory/format'
import { findWeaponDetails } from 'modules/publicVariables/weapon'

const formatBill = bill => {
  bill.amountFormatted = formatPrice(bill.amount)
  bill.dateFormatted = moment(bill.insertTime).format('DD/MM/YYYY HH:mm')

  return bill
}

export const formatRefund = refund => {
  refund.amountFormatted = formatPrice(refund.amount)
  refund.dateFormatted = moment(refund.createdAt).format('DD/MM/YYYY HH:mm')
  return refund
}

const formatLicense = license => {
  license.dateFormatted = moment(license.insertTime).format('DD/MM/YYYY HH:mm')
  license.expirationFormatted = license.expiration ? moment(license.expiration).format('DD/MM/YYYY HH:mm') : "-"
  return license
}

const formatPlayerBan = (ban) => {
  ban.isPermanent = ban.permanent
  ban.expirationFormatted = moment(ban.expiration).format('DD/MM/YYYY HH:mm')
  ban.isBanByConsole = ban.banner?.fullName === 'Console'
  ban.explaination = `Banni ${ban.isBanByConsole ? 'automatiquement' : `par ${ban.banner?.fullName}`
    }${' '}
                          ${ban.isPermanent
      ? 'de manière permanente'
      : `jusqu'au ${ban.expirationFormatted}`
    }${' '}
                          pour la raison: ${ban.reason}`
  return ban
}

export const formatPlayer = createFormatter(
  player => {
    // TRICK: bad response from the API if the player has never logged in
    if (Array.isArray(player) && player.length === 0) {
      return {
        hasNeverLoggedIn: true,
        isBan: true,
      }
    }

    return player
  },
  formatPlayerAndRconPlayer,
  (player, { jobs: jobsLegacyParam, publicVariables : publicVariablesParam }) => {
    const publicVariables = publicVariablesParam || {
      weapons: [],
      items: [],
      jobs: jobsLegacyParam || [],
    }
    // FIXME: better fix
    const jobs = publicVariables?.jobs || jobsLegacyParam

    if (!player) {
      return null
    }
    player.id = player.identifier

    player.groupLabel = getGroupLabel(player.group)

    player.spyJoinDateFormatted = null
    player.spyJoinDateFormattedWithTime = null
    if (player.spyJoinDate && player.insertTime) {
      const date = player.spyJoinDate < player.insertTime ? player.spyJoinDate : player.insertTime
      player.spyJoinDateFormatted = moment(date).format('DD/MM/YYYY')
      player.spyJoinDateFormattedWithTime = moment(date).format('DD/MM/YYYY HH:mm')
    } else if (player.spyJoinDate) {
      player.spyJoinDateFormatted = moment(player.spyJoinDate).format('DD/MM/YYYY')
      player.spyJoinDateFormattedWithTime = moment(player.spyJoinDate).format('DD/MM/YYYY HH:mm')
    } else if (player.insertTime) {
      player.spyJoinDateFormatted = moment(player.insertTime).format('DD/MM/YYYY')
      player.spyJoinDateFormattedWithTime = moment(player.insertTime).format('DD/MM/YYYY HH:mm')
    }

    player.spyLastSeenFormmated = null
    player.spyLastSeenFormmatedWithTime = null
    if (player.spyLastSeen) {
      player.spyLastSeenFormmated = moment(player.spyLastSeen).format('DD/MM/YYYY')
      player.spyLastSeenFormmatedWithTime = moment(player.spyLastSeen).format('DD/MM/YYYY HH:mm')
    } else if (player.lastSeen) {
      player.spyLastSeenFormmated = moment(player.lastSeen).format('DD/MM/YYYY')
      player.spyLastSeenFormmatedWithTime = moment(player.lastSeen).format('DD/MM/YYYY HH:mm')
    }
    
    player.isWiped = player.fullName === player.identifier || player.fullName.includes("(Ancien nom)") || player.fullName.includes("(Wipe)")

    player.isGroupUser = player.group === Group.USER
    player.isGroupSupport = player.group === Group.SUPPORT
    player.isGroupMod = player.group === Group.MOD
    player.isGroupSupermod = player.group === Group.SUPERMOD
    player.isGroupStaff = player.group === Group.STAFF
    player.isGroupAdmin = player.group === Group.ADMIN

    player.isDead = player.isDead === 1 || player.isDead === true
    player.isAlive = !player.isDead
    player.lifeStatus = player.isDead ? 'Mort' : 'Vivant'

    // min to ms
    player.hasJailTime = player.jailTime > 0
    player.jailTimeFormatted = millisecondsToStr(player.jailTime * 1000)

    player.loggedInStatusLabel = player.isInGame ? 'Connecté' : 'Déconnecté'
    player.donatorLvlLabel = player.donatorlvl ? `Donateur ${player.donatorlvl}` : 'Pas donateur'

    player.firstName = player.firstname || ''
    player.lastName = player.lastname || ''
    player.fullName = player.fullName = player.fullName || `${player.firstName} ${player.lastName}`

    // m / f
    player.isFemale = player.sex !== 'm'
    player.isMale = player.sex === 'm'
    player.genderLabel = player.sex === 'm' ? 'Homme' : 'Femme'

    // min to ms
    player.timePlayedFormatted = millisecondsToStr(player.playTime * 1000)

    player.ips = player.ips || []

    player.avatarLabel = `${player.firstName[0]}${player.lastName[0]}`
    player.avatarUrl = `https://api.dicebear.com/9.x/initials/svg?seed=${player.firstName} ${player.lastName}`
    player.accounts = {
      blackMoney: player.accounts?.blackMoney || 0,
      blackMoneyFormatted: formatPrice(player.accounts?.blackMoney),

      money: player.accounts?.money || 0,
      moneyFormatted: formatPrice(player.accounts?.money),

      bank: player.accounts?.bank || 0,
      bankFormatted: formatPrice(player.accounts?.bank),

      chips: player.accounts?.chips || 0,
      chipsFormatted: formatPrice(player.accounts?.chips || 0, ''),

      poker_chips: player.accounts?.poker_chips || 0,
      pokerChipsFormatted: formatPrice(player.accounts?.poker_chips || 0, ''),
    }

    player.totalMoneyFormatted = formatPrice(player.totalMoney)

    // Ranking money avec jetons (1 jeton = 5$)
    player.totalRealMoney = Number(player.total_real_money) || player.totalMoney || 0
    player.totalRealMoneyFormatted = formatPrice(player.totalRealMoney)
    player.rankingChips = Number(player.chips) || player.accounts?.chips || 0
    player.rankingChipsFormatted = player.rankingChips.toLocaleString('fr-FR')
    player.chipsValue = Number(player.chipsValue) || (player.rankingChips * 5)
    player.chipsValueFormatted = formatPrice(player.chipsValue)
    player.totalMoneyWithChips = Number(player.total_money) || (player.totalRealMoney + player.chipsValue)
    player.totalMoneyWithChipsFormatted = formatPrice(player.totalMoneyWithChips)

    player.jobData = getJobData(jobs, player.job, player.jobGrade)
    player.jobLabel = player.jobLabel || player.jobData?.job?.label || player.job || 'Aucun'
    player.jobGradeLabel = player.jobGradeLabel || player.jobData?.label || player.jobGrade || 'Aucun'

    player.job2Data = getJobData(jobs, player.job2, player.job2Grade)
    player.job2Label = player.job2Label || player.job2Data?.job?.label || player.job2 || 'Aucun'
    player.job2GradeLabel = player.job2GradeLabel || player.job2Data?.label || player.job2Grade || 'Aucun'
    player.jobNames = [player.job, player.job2].filter(Boolean)

    const UNKNOWN_PERCENT = ' - '
    const NONE_PERCENT = '0'
    const DEFAULT_STATUS_DATA = {
      percent: UNKNOWN_PERCENT,
    }

    const currTime = Math.floor(Date.now() / 1000);
    let currentPtsdStatusValue = {
      percent: NONE_PERCENT
    }
    const currentPtsdStatus = findOnArray(player.status, (status) => status.name === "ptsd");
    if (currentPtsdStatus && (!currentPtsdStatus.timeout || (currentPtsdStatus.timeout > currTime))) {
      currentPtsdStatusValue = currentPtsdStatus
    }

    player.statusData = {
      hunger:
        findOnArray(player.status, (status) => status.name === 'hunger') || DEFAULT_STATUS_DATA,
      thirst:
        findOnArray(player.status, (status) => status.name === 'thirst') || DEFAULT_STATUS_DATA,
      drunk: findOnArray(player.status, (status) => status.name === 'drunk') || DEFAULT_STATUS_DATA,
      armour:
        findOnArray(player.status, (status) => status.name === 'armour') || DEFAULT_STATUS_DATA,
      health:
        findOnArray(player.status, (status) => status.name === 'health') || DEFAULT_STATUS_DATA,
      muscle:
        findOnArray(player.status, (status) => status.name === 'muscle') || DEFAULT_STATUS_DATA,
      zen:
        findOnArray(player.status, (status) => status.name === 'zen') || DEFAULT_STATUS_DATA,
      blood:
        findOnArray(player.status, (status) => status.name === 'blood') || DEFAULT_STATUS_DATA,
      ptsd: currentPtsdStatusValue
    }

    function formatStatusData(varName) {
      if (
        player.statusData[varName]?.percent &&
        player.statusData[varName].percent !== UNKNOWN_PERCENT
      ) {
        player.statusData[varName].percent = !isString(player.statusData[varName].percent)
          ? Math.ceil(Number(player.statusData[varName].percent)).toFixed(0)
          : player.statusData[varName].percent
      }
    }

    formatStatusData('hunger')
    formatStatusData('thirst')
    formatStatusData('drunk')
    formatStatusData('armour')
    formatStatusData('health')
    formatStatusData('muscle')
    formatStatusData('zen')
    formatStatusData('blood')
    formatStatusData('ptsd')

    // Formater les inventaires du joueur
    formatPlayerInventoriesData(player, { publicVariables })

    // transform object into array
    // player.skinData = map(player.skin || {}, (value, id) => {
    //   return {
    //     id,
    //     value,
    //   }
    // })

    player.billsTotalAmountFormatted = formatPrice(player.bills?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0
    ));
    player.hasBills = !isEmpty(player.bills)
    player.nonRadarBills = player.bills?.filter(e => !e.isRadar);
    player.radarBills = player.bills?.filter(e => e.isRadar);

    player.radarBillsTotalAmountFormatted = formatPrice(player.radarBills?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0
    ));

    player.hasRefunds = !isEmpty(player.refunds)
    player.hasUser = !!player.user
    return player
  },
  createSubObjectFormatter('user', formatUser),
  createSubObjectFormatter('ban', formatPlayerBan),
  createSubObjectFormatter('bills', createListFormatter(formatBill)),
  createSubObjectFormatter('refunds', createListFormatter(formatRefund)),
  createSubObjectFormatter('licenses', createListFormatter(formatLicense)),

  createSubObjectFormatter('properties', createListFormatter((property, { publicVariables }) => {
    return formatProperty(property, { publicVariables })
  }))
)

// Fonction réutilisable pour formater les inventaires d'un joueur
const formatPlayerInventoriesData = (player, { publicVariables }) => {
  // transform object into array
  player.loadoutData = map(player.loadout || {}, (data, name) => {
    return {
      name,
      ammo: data.ammo,
      data: {
        sn: data.data.sn,
        durability: data.data.durability
      },
      ...(findWeaponDetails(publicVariables.weapons, name) || {}),
    }
  })
  player.loadoutData = sortBy(player.loadoutData, i => i.label?.toLowerCase())

  // transform object into array
  const inventoryData = []
  forEach(player.inventory, formatInventoryInventory(publicVariables, inventoryData))
  player.inventoryData = inventoryData

  player.inventoryData = sortBy(player.inventoryData, i => i.label?.toLowerCase())
  player.hasBag = !!player.bagInventory
  player.hasAnus = !!player.anusInventory

  // TODO: use inventory formatter
  // format data of the player to be like the other inventories
  player.inventory = {
    inventoryData: player.inventoryData,
    loadoutData: player.loadoutData,

    ...player.accounts,
  }

  return player
}

// Formatter dédié pour les propriétés (réutilisable pour GET_PLAYER_PROPERTIES)
export const formatProperty = (property, { publicVariables }) => {
  property.label = property.propertyId
  property.type = property.propertyType
  
  if (!property.inventories) {
    property.inventories = {}
  }

  property.inventories = map(property.inventories, (inventory) => {
    inventory = formatInventory(inventory, { publicVariables })
    return inventory
  })

  // Boîte aux lettres - Exclue pour les garages
  if (!property.type || !property.type.toLowerCase().includes("garage")) {
    property.mailbox = formatInventory(property.mailbox, { publicVariables })
  }
  
  property.props = map(property.props, (prop) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    return prop
  })
  
  return property
}

// Formatter pour une liste de propriétés (pour GET_PLAYER_PROPERTIES)
export const formatProperties = createListFormatter((property, { publicVariables }) => {
  return formatProperty(property, { publicVariables })
})

// Formatter pour les licenses du joueur (pour GET_PLAYER_LICENSES)
export const formatPlayerLicenses = createFormatter(
  (data) => {
    if (!data) return data
    return data
  },
  createSubObjectFormatter('licenses', createListFormatter(formatLicense))
)

// Formatter pour les bills du joueur (pour GET_PLAYER_BILLS)
export const formatPlayerBills = createFormatter(
  (data) => {
    if (!data) return data
    
    // Calculer les totaux comme dans formatPlayer
    data.billsTotalAmountFormatted = formatPrice(data.bills?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0
    ));
    data.hasBills = !isEmpty(data.bills)
    data.nonRadarBills = data.bills?.filter(e => !e.isRadar);
    data.radarBills = data.bills?.filter(e => e.isRadar);

    data.radarBillsTotalAmountFormatted = formatPrice(data.radarBills?.reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0
    ));
    
    return data
  },
  createSubObjectFormatter('bills', createListFormatter(formatBill))
)

// Formatter pour les inventaires du joueur (pour GET_PLAYER_INVENTORIES)
export const formatPlayerInventories = createFormatter(
  (data, { publicVariables }) => {
    if (!data) return data
    
    // Réutiliser EXACTEMENT la même logique que formatPlayer
    formatPlayerInventoriesData(data, { publicVariables })
    return data
  },
  createSubObjectFormatter('bagInventory', createFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    prop.content.name = prop.name
    prop.moneyFormatted = formatPrice(prop.money || 0)
    prop.blackMoneyFormatted = formatPrice(prop.blackMoney || 0)
    return prop
  })),
  createSubObjectFormatter('anusInventory', createFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    prop.content.name = prop.name
    prop.moneyFormatted = formatPrice(prop.money || 0)
    prop.blackMoneyFormatted = formatPrice(prop.blackMoney || 0)
    return prop
  })),
  createSubObjectFormatter('propsInventories', createListFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    return prop
  })),
  createSubObjectFormatter('donatorFarmsInventories', createListFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    return prop
  })),
  createSubObjectFormatter('motelInvInventories', createListFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    return prop
  })),
  createSubObjectFormatter('lockerInventories', createListFormatter((prop, { publicVariables }) => {
    prop.content = formatInventory(prop.content, { publicVariables })
    return prop
  })),
  createSubObjectFormatter('racksInventories', createListFormatter((rack, { publicVariables }) => {
    // Formater les armes dans le rack
    if (rack.rack) {
      rack.rack = map(rack.rack, (slot) => {
        if (slot.weapon && slot.weapon.name) {
          return {
            ...slot,
            weapon: {
              ...slot.weapon,
              ...(findWeaponDetails(publicVariables.weapons, slot.weapon.name) || {}),
            }
          }
        }
        return slot
      })
    }
    return rack
  })),
  createSubObjectFormatter('housesInventoryItems', createListFormatter((item, { publicVariables }) => {
    return {
      ...item,
      quantity: item.amount ?? item.count,
      ...((publicVariables.items || []).find((i) => i.name === item.name) || {}),
    }
  }))
)
